import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Star, Share2, X, Volume2, Square, ChevronLeft, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Cache de l'ordre de lecture (liste ordonnee des ids) par domaine, pour
// naviguer article precedent/suivant sans refetch a chaque swipe.
const readingOrderCache = {};

// Aplati la hierarchie {livre:{titre:{chapitre:[articles]}}} dans l'ordre
// exact d'affichage de l'explorateur -> tableau ordonne d'ids.
const flattenHierarchy = (hierarchy) => {
  const ids = [];
  Object.values(hierarchy || {}).forEach((titres) =>
    Object.values(titres || {}).forEach((chapitres) =>
      Object.values(chapitres || {}).forEach((articles) =>
        (articles || []).forEach((a) => ids.push(a.id))
      )
    )
  );
  return ids;
};

const DOMAIN_LABELS = {
  constitution: 'Constitution',
  famille: 'Code de la Famille',
  penal: 'Code Penal',
  travail: 'Code du Travail',
  investissement: 'Code des Investissements',
  numerique: 'Code du Numerique',
  procedure_penale: 'Code de Procedure Penale',
  protection_enfant: "Loi sur la Protection de l'Enfant",
  foncier: 'Code Foncier et Immobilier',
  entrepreneuriat: "Loi sur l'Entrepreneuriat et les Startups",
  baux_loyers: 'Baux à Loyers',
  obligations: 'Code des Contrats et Obligations',
  barreau: 'Organisation du Barreau',
  minier: 'Code Minier',
  procedure_civile: 'Code de Procédure Civile',
};

const getFavorites = () => {
  try {
    return JSON.parse(localStorage.getItem('mibeko_favorites') || '[]');
  } catch { return []; }
};

const toggleFavorite = (articleId) => {
  const favs = getFavorites();
  const idx = favs.indexOf(articleId);
  if (idx > -1) {
    favs.splice(idx, 1);
  } else {
    favs.push(articleId);
  }
  localStorage.setItem('mibeko_favorites', JSON.stringify(favs));
  return favs;
};

const ArticlePage = () => {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFav, setIsFav] = useState(false);
  const [defTerm, setDefTerm] = useState(null);
  const [defText, setDefText] = useState('');
  const [defLoading, setDefLoading] = useState(false);
  const [sumLang, setSumLang] = useState(() => {
    try { return localStorage.getItem('mibeko_summary_lang') || 'fr'; } catch { return 'fr'; }
  });
  const [translated, setTranslated] = useState({});
  const [sumLoading, setSumLoading] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const audioRef = React.useRef(null);
  const [neighbors, setNeighbors] = useState({ prev: null, next: null });
  const touchStart = React.useRef(null);
  const [slideDir, setSlideDir] = useState(null); // 'next' | 'prev' | null

  const goToArticle = (id, dir) => {
    if (!id) return;
    stopAudio();
    setSlideDir(dir || null);
    navigate(`/article/${id}`, { replace: true });
  };

  const onTouchStart = (e) => {
    const t = e.changedTouches[0];
    touchStart.current = { x: t.clientX, y: t.clientY };
  };

  const onTouchEnd = (e) => {
    if (!touchStart.current || defTerm) { touchStart.current = null; return; }
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStart.current.x;
    const dy = t.clientY - touchStart.current.y;
    touchStart.current = null;
    // Uniquement les swipes franchement horizontaux
    if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy) * 1.4) return;
    if (dx < 0) goToArticle(neighbors.next, 'next');  // swipe vers la GAUCHE -> suivant
    else goToArticle(neighbors.prev, 'prev');         // swipe vers la DROITE -> precedent
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if ('speechSynthesis' in window) window.speechSynthesis.cancel();
    setSpeaking(false);
  };

  const deviceVoiceFallback = () => {
    if (!('speechSynthesis' in window)) { setSpeaking(false); return; }
    const text = currentSummaryText();
    if (!text) { setSpeaking(false); return; }
    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = { fr: 'fr-FR', ln: 'fr-CD', sw: 'sw' }[sumLang] || 'fr-FR';
    utter.rate = 0.95;
    utter.onend = () => setSpeaking(false);
    utter.onerror = () => setSpeaking(false);
    window.speechSynthesis.speak(utter);
  };

  const toggleSpeech = () => {
    if (speaking) { stopAudio(); return; }
    if (!currentSummaryText()) return;
    setSpeaking(true);
    // Voix naturelle IA (OpenAI TTS) ; repli sur la voix du telephone si echec
    const audio = new Audio(`${API_URL}/api/articles/${articleId}/audio?lang=${sumLang}`);
    audioRef.current = audio;
    audio.onended = () => setSpeaking(false);
    audio.onerror = () => { audioRef.current = null; deviceVoiceFallback(); };
    audio.play().catch(() => { audioRef.current = null; deviceVoiceFallback(); });
  };

  useEffect(() => () => {
    if (audioRef.current) audioRef.current.pause();
    if ('speechSynthesis' in window) window.speechSynthesis.cancel();
  }, []);

  const currentSummaryText = () => {
    if (!article) return '';
    return sumLang === 'fr' ? article.summary : (translated[sumLang] || '');
  };


  const switchLang = async (lang) => {
    setSumLang(lang);
    try { localStorage.setItem('mibeko_summary_lang', lang); } catch {}
    if (lang === 'fr' || translated[lang]) return;
    setSumLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/articles/${articleId}/summary?lang=${lang}`);
      const data = await r.json();
      setTranslated(prev => ({ ...prev, [lang]: r.ok ? data.summary : null }));
    } catch {
      setTranslated(prev => ({ ...prev, [lang]: null }));
    } finally {
      setSumLoading(false);
    }
  };

  useEffect(() => {
    if (article && sumLang !== 'fr' && !translated[sumLang]) switchLang(sumLang);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [article]);

  const openDefinition = async (kw) => {
    setDefTerm(kw);
    setDefText('');
    setDefLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/define?term=${encodeURIComponent(kw)}`);
      const data = await r.json();
      setDefText(r.ok ? data.definition : "Définition indisponible pour le moment, réessayez.");
    } catch {
      setDefText("Définition indisponible pour le moment, réessayez.");
    } finally {
      setDefLoading(false);
    }
  };

  useEffect(() => {
    stopAudio();
    setIsFav(getFavorites().includes(articleId));
    fetch(`${API_URL}/api/articles/${articleId}`)
      .then(r => r.json())
      .then(data => { setArticle(data); setLoading(false); })
      .catch(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articleId]);

  // Calcule les articles precedent / suivant dans l'ordre du code (pour le swipe)
  useEffect(() => {
    if (!article || !article.domain) return;
    const domain = article.domain;
    const compute = (ids) => {
      const idx = ids.indexOf(articleId);
      setNeighbors({
        prev: idx > 0 ? ids[idx - 1] : null,
        next: idx >= 0 && idx < ids.length - 1 ? ids[idx + 1] : null,
      });
    };
    if (readingOrderCache[domain]) { compute(readingOrderCache[domain]); return; }
    fetch(`${API_URL}/api/hierarchy/${domain}`)
      .then(r => r.json())
      .then(data => {
        const ids = flattenHierarchy(data.hierarchy);
        readingOrderCache[domain] = ids;
        compute(ids);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [article, articleId]);

  const handleToggleFav = () => {
    const favs = toggleFavorite(articleId);
    setIsFav(favs.includes(articleId));
  };

  if (loading) return <div className="loading-spinner" />;
  if (!article) return (
    <div className="empty-state">
      <h2>Article non trouve</h2>
      <p>Cet article n'existe pas dans la base de donnees.</p>
    </div>
  );

  return (
    <div onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} data-testid="article-swipe-container">
      {/* Header */}
      <div className="back-header">
        <button className="back-btn" onClick={() => navigate(-1)} data-testid="back-btn-article">
          <ArrowLeft />
        </button>
        <h1>{article.article_number}</h1>
      </div>

      <div
        className={`article-slide ${slideDir === 'next' ? 'slide-next' : slideDir === 'prev' ? 'slide-prev' : ''}`}
        key={article.id}
        data-testid="article-slide"
      >
      <div className="blue-header" data-testid="article-header">
        <h1>{article.article_number}</h1>
        <p>{article.domain_label || DOMAIN_LABELS[article.domain] || article.domain}</p>
        {article.titre && <p style={{ fontSize: '12px', color: 'var(--text-light)' }}>{article.titre}</p>}
      </div>

      {defTerm && (
        <div
          data-testid="keyword-definition-overlay"
          onClick={() => setDefTerm(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 10000,
            background: 'rgba(11, 18, 32, 0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px',
          }}
        >
          <div
            data-testid="keyword-definition-modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--bg-white)', borderRadius: '16px',
              padding: '24px 20px', maxWidth: '400px', width: '100%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)', position: 'relative',
            }}
          >
            <button
              onClick={() => setDefTerm(null)}
              data-testid="keyword-definition-close-btn"
              aria-label="Fermer"
              style={{
                position: 'absolute', top: '10px', right: '10px',
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--text-gray)', padding: '4px', display: 'flex',
              }}
            >
              <X style={{ width: 18, height: 18 }} />
            </button>
            <h3 style={{ fontSize: '16px', fontWeight: 700, color: 'var(--primary)', marginBottom: '10px', textTransform: 'capitalize' }}>
              {defTerm}
            </h3>
            {defLoading ? (
              <p style={{ fontSize: '14px', color: 'var(--text-gray)' }}>Recherche de la définition…</p>
            ) : (
              <p data-testid="keyword-definition-text" style={{ fontSize: '14px', lineHeight: 1.65, color: 'var(--text-dark)' }}>
                {defText}
              </p>
            )}
          </div>
        </div>
      )}

      <div className="article-detail">
        {/* Actions */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
          <button
            className={`fav-btn ${isFav ? 'active' : ''}`}
            onClick={handleToggleFav}
            data-testid="fav-btn"
          >
            <Star style={{ width: '16px', height: '16px', fill: isFav ? 'currentColor' : 'none' }} />
            {isFav ? 'Sauvegarde' : 'Sauvegarder'}
          </button>
        </div>

        {/* Summary */}
        {article.summary && (
          <div className="article-summary-box" data-testid="article-summary">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
              <h3>Resume simplifie</h3>
              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }} data-testid="summary-lang-switcher">
                {(
                  <button
                    onClick={toggleSpeech}
                    data-testid="summary-audio-btn"
                    aria-label={speaking ? 'Arrêter la lecture' : 'Écouter le résumé'}
                    title={speaking ? 'Arrêter la lecture' : 'Écouter le résumé'}
                    style={{
                      padding: '5px 8px', borderRadius: '999px', cursor: 'pointer',
                      display: 'flex', alignItems: 'center',
                      border: `1px solid ${speaking ? 'var(--primary)' : 'var(--border)'}`,
                      background: speaking ? 'var(--primary)' : 'transparent',
                      color: speaking ? '#fff' : 'var(--text-gray)',
                    }}
                  >
                    {speaking ? <Square style={{ width: 12, height: 12 }} /> : <Volume2 style={{ width: 14, height: 14 }} />}
                  </button>
                )}
                {[['fr', 'FR'], ['ln', 'Lingala'], ['sw', 'Kiswahili']].map(([code, label]) => (
                  <button
                    key={code}
                    onClick={() => switchLang(code)}
                    data-testid={`summary-lang-${code}`}
                    style={{
                      padding: '4px 10px', borderRadius: '999px', cursor: 'pointer',
                      fontSize: '11px', fontWeight: 700, fontFamily: 'inherit',
                      border: `1px solid ${sumLang === code ? 'var(--primary)' : 'var(--border)'}`,
                      background: sumLang === code ? 'var(--primary)' : 'transparent',
                      color: sumLang === code ? '#fff' : 'var(--text-gray)',
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <p data-testid="summary-text">
              {sumLang === 'fr'
                ? article.summary
                : sumLoading
                  ? 'Traduction en cours…'
                  : (translated[sumLang] || "Traduction indisponible, réessayez.")}
            </p>
          </div>
        )}

        {/* Full Text */}
        <h3 style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '8px' }}>
          Texte integral
        </h3>
        <div className="article-text" data-testid="article-text">
          {article.text_integral}
        </div>

        {/* Keywords */}
        {article.keywords && article.keywords.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '10px' }}>
              Mots-cles juridiques
            </h3>
            <div className="keyword-list" data-testid="article-keywords">
              {article.keywords.map(kw => (
                <button
                  key={kw}
                  className="keyword-item"
                  data-testid={`keyword-${kw.replace(/\s+/g, '-')}`}
                  onClick={() => openDefinition(kw)}
                  style={{ cursor: 'pointer', border: 'none', fontFamily: 'inherit' }}
                >
                  {kw}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Meta */}
        <div className="article-meta" data-testid="article-meta">
          {article.livre && <span className="article-tag">{article.livre}</span>}
          {article.chapitre && <span className="article-tag">{article.chapitre}</span>}
        </div>

        {/* Navigation precedent / suivant (aussi accessible au swipe) */}
        <div className="article-nav" data-testid="article-nav">
          <button
            className="article-nav-btn"
            onClick={() => goToArticle(neighbors.prev, 'prev')}
            disabled={!neighbors.prev}
            data-testid="article-nav-prev"
          >
            <ChevronLeft style={{ width: 18, height: 18 }} />
            Precedent
          </button>
          <button
            className="article-nav-btn"
            onClick={() => goToArticle(neighbors.next, 'next')}
            disabled={!neighbors.next}
            data-testid="article-nav-next"
          >
            Suivant
            <ChevronRight style={{ width: 18, height: 18 }} />
          </button>
        </div>
      </div>
      </div>
    </div>
  );
};

export default ArticlePage;
